import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { ScrapStatus } from '../../@types';
import {
  useGetDraftContentStatus,
  useCreateContentFromUrl,
  useCancelDraft,
  useGetPublicAvatarData,
  useGetPublicAudios,
} from '../../hooks';

interface UseGenerateDraftFromUrlProps {
  url: string;
  name: string;
  isOpen: boolean;
  onClose: () => void;
  refetchDraftData: () => void;
}

const POLLING_INTERVAL = 10000;

export const useGenerateDraftFromUrl = ({
  url,
  name,
  isOpen,
  onClose,
  refetchDraftData,
}: UseGenerateDraftFromUrlProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [creationId, setCreationId] = useState('');
  const [polling, setPolling] = useState(false);

  const { mutate: mutateCreateContentFromUrl } = useCreateContentFromUrl();
  const { mutate: mutateGetContentStatus } = useGetDraftContentStatus();
  const { mutate: mutateCancelDraft } = useCancelDraft();
  const { data: publicAvatarData } = useGetPublicAvatarData();
  const { data: publicAudioData } = useGetPublicAudios();

  const { enqueueSnackbar } = useSnackbar();

  const handleClearData = () => {
    setIsProcessing(false);
    setPolling(false);
    setCreationId('');
    setDraftContent('');
  };

  const handleOnClose = () => {
    onClose();

    if (isProcessing) {
      mutateCancelDraft({
        creationId: creationId,
        url,
        isAlreadyDraft: false,
      });
    }
    handleClearData();
  };

  const checkCreationStatus = (url: string) => {
    mutateGetContentStatus(
      { url },
      {
        onSuccess: (data) => {
          if (data.status === ScrapStatus.Success) {
            setIsProcessing(false);
            if (data.scrapedContent) {
              setDraftContent(data.scrapedContent);
              refetchDraftData();
            } else {
              enqueueSnackbar(
                'We are unable to scrape the given website. Please try another one.',
                {
                  variant: 'error',
                }
              );
            }
            setPolling(false);
          } else if (data.status === ScrapStatus.Error) {
            setIsProcessing(false);
            enqueueSnackbar('We are unable to scrape the given website. Please try another one.', {
              variant: 'error',
            });
            setPolling(false);
          }
        },
        onError: (err: any) => {
          setIsProcessing(false);
          setPolling(false);
          onClose();
          if (polling) enqueueSnackbar('We are unable to scrape the given website. Please try another one.', { variant: 'error' });
        },
      }
    );
  };

  const onSubmit = () => {
    setIsProcessing(true);
    mutateCreateContentFromUrl(
      {
        url,
        audioId: publicAudioData?.[0]?.id,
        avatarId: publicAvatarData?.[0]?.id,
        videoName: name,
      },
      {
        onSuccess: (data) => {
          if ('content' in data) {
            if (data?.content) {
              setDraftContent(data?.content);
              setCreationId(data?.id);
              setIsProcessing(false);
            } else {
              setIsProcessing(false);
              enqueueSnackbar(
                'We are unable to scrape the given website. Please try another one.',
                {
                  variant: 'error',
                }
              );
            }
          } else {
            setCreationId(data?.id);
            setPolling(true);
          }
        },
        onError: (err: any) => {
          setIsProcessing(false);
          enqueueSnackbar(err?.response?.data?.message || err?.message || 'Something went wrong', {
            variant: 'error',
          });
          setPolling(false);
        },
      }
    );
  };

  const handleOnMinimize = () => {
    onClose();
    handleClearData();
    refetchDraftData();
  };

  const handleOnSuccessEdit = () => {
    refetchDraftData();
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (polling) {
      intervalId = setInterval(() => {
        checkCreationStatus(url);
      }, POLLING_INTERVAL);
    } else {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [polling]);

  return {
    isProcessing,
    draftContent,
    onSubmit,
    handleOnClose,
    handleOnMinimize,
    selectedAvatar: publicAvatarData?.[0],
    selectedAudio: publicAudioData?.[0],
    creationId,
    handleOnSuccessEdit,
  };
};
